import React, { useState } from 'react';
import { submitQuote, pollQuoteStatus, searchLead } from '../api/client';
import PhotoUpload from '../components/PhotoUpload';
import ColorSelector from '../components/ColorSelector';

export default function WorkerView() {


    const [formData, setFormData] = useState({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        customerAddress: '',
        measurements: {
            frontWall: { length: '', height: '' },
            leftWall: { length: '', height: '' },
            rightWall: { length: '', height: '' },
            backWall: { length: '', height: '' },
            stories: 1,
            trimLinearFeet: '',
            gutterLinearFeet: '',
            soffitLinearFeet: '',
            soffitAvgWidthFt: '',
            windowCount: '',
            doorCount: '',
            garageDoorCount: '',
            shutterCount: '',
            fasciaLinearFeet: '',
            columnsPillarsCount: '',
            columnsPillarsAvgSize: '',
            porchCeilingsFront: '',
            porchCeilingsBack: '',
            poolDeckSqft: '',
            twoToneGuttersSoffits: false
        },
        colors: {
            mainHouse: { code: '', name: '', hex: '' },
            trim: { code: '', name: '', hex: '' },
            gutters: { code: '', name: '', hex: '' },
            soffits: { code: '', name: '', hex: '' },
            fascia: { code: '', name: '', hex: '' },
            doors: { code: '', name: '', hex: '' },
            garageDoors: { code: '', name: '', hex: '' },
            shutters: { code: '', name: '', hex: '' },
            porchCeilings: { code: '', name: '', hex: '' },
            colorComplexity: 'standard'
        }
    });

    const [photos, setPhotos] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitResult, setSubmitResult] = useState(null);
    const [consentChecked, setConsentChecked] = useState(false);
    const [paintTier, setPaintTier] = useState('superpaint');
    const [lightColorPrimer, setLightColorPrimer] = useState(false);

    // Lead lookup state
    const [lookupEmail, setLookupEmail] = useState('');
    const [lookupPhone, setLookupPhone] = useState('');
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupResult, setLookupResult] = useState(null);
    const [leadId, setLeadId] = useState(null);
    const [multiColor, setMultiColor] = useState(false);
    const [sameWallHeight, setSameWallHeight] = useState(false);


    const paintTiers = [
        { value: 'a100', name: 'A-100 (Budget)', rate: 1.35 },
        { value: 'superpaint', name: 'SuperPaint (Standard)', rate: 1.55, badge: 'Most Popular' },
        { value: 'latitude', name: 'Latitude (Premium)', rate: 1.80 },
        { value: 'emerald', name: 'Emerald Rain Refresh (Ultimate)', rate: 2.00 }
    ];

    const wallKeys = ['frontWall', 'leftWall', 'rightWall', 'backWall'];

    const handleWallFieldChange = (wallKey, field, value) => {
        setFormData(prev => {
            const nextMeasurements = { ...prev.measurements };

            if (field === 'height' && sameWallHeight) {
                wallKeys.forEach((key) => {
                    nextMeasurements[key] = { ...nextMeasurements[key], height: value };
                });
            } else {
                nextMeasurements[wallKey] = { ...nextMeasurements[wallKey], [field]: value };
            }

            return { ...prev, measurements: nextMeasurements };
        });
    };

    const handleSameHeightToggle = (checked) => {
        setSameWallHeight(checked);

        if (!checked) {
            return;
        }

        setFormData(prev => {
            const sourceHeight = wallKeys
                .map((key) => prev.measurements[key].height)
                .find((value) => value !== '' && value !== null && value !== undefined);

            if (sourceHeight === undefined) {
                return prev;
            }

            const nextMeasurements = { ...prev.measurements };
            wallKeys.forEach((key) => {
                nextMeasurements[key] = { ...nextMeasurements[key], height: sourceHeight };
            });

            return { ...prev, measurements: nextMeasurements };
        });
    };

    const handleCountInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            measurements: { ...prev.measurements, [field]: value }
        }));
    };

    const handleCountStep = (field, delta) => {
        setFormData(prev => {
            const currentValue = parseInt(prev.measurements[field], 10);
            const safeCurrent = Number.isFinite(currentValue) ? currentValue : 0;
            const nextValue = Math.max(0, safeCurrent + delta);

            return {
                ...prev,
                measurements: { ...prev.measurements, [field]: String(nextValue) }
            };
        });
    };

    const CountStepper = ({ id, label, value, onChange, onStep }) => (
        <div className="count-stepper">
            <label htmlFor={id}>{label}</label>
            <div className="count-stepper-controls">
                <button
                    type="button"
                    className="count-stepper-button"
                    onClick={() => onStep(-1)}
                    aria-label={`Decrease ${label}`}
                >
                    -
                </button>
                <input
                    id={id}
                    type="number"
                    min="0"
                    step="1"
                    placeholder="0"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                />
                <button
                    type="button"
                    className="count-stepper-button"
                    onClick={() => onStep(1)}
                    aria-label={`Increase ${label}`}
                >
                    +
                </button>
            </div>
        </div>
    );

    const handleLeadLookup = async () => {
        if (!lookupEmail && !lookupPhone) {
            setLookupResult({ found: false, message: 'Enter an email or phone to search' });
            return;
        }

        setLookupLoading(true);
        setLookupResult(null);

        try {
            const result = await searchLead(lookupEmail, lookupPhone);
            setLookupResult(result);

            if (result.found && result.lead) {
                // Pre-populate form with lead data
                setFormData(prev => ({
                    ...prev,
                    customerName: result.lead.customerName || prev.customerName,
                    customerEmail: result.lead.customerEmail || prev.customerEmail,
                    customerPhone: result.lead.customerPhone || prev.customerPhone,
                    customerAddress: result.lead.customerAddress || prev.customerAddress
                }));
                setLeadId(result.lead.leadId);

                // If lead has paint quality preference, set it
                if (result.lead.paintQuality) {
                    setPaintTier(result.lead.paintQuality);
                }
            }
        } catch (error) {
            setLookupResult({ found: false, message: error.message || 'Search failed' });
        } finally {
            setLookupLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const requiresConsent = photos.length > 0;

        if (requiresConsent && !consentChecked) {
            alert('Please confirm customer consent for AI photo analysis');
            return;
        }




        setSubmitting(true);
        setSubmitResult(null);

        try {
            const colorComplexity = multiColor ? 'complex' : (lightColorPrimer ? 'light' : 'standard');

            // Convert measurements to proper types
            // Include leadId if we found an existing lead
            const baseMeasurements = {
                ...formData.measurements,
                frontWall: {
                    length: parseFloat(formData.measurements.frontWall.length),
                    height: parseFloat(formData.measurements.frontWall.height)
                },
                leftWall: {
                    length: parseFloat(formData.measurements.leftWall.length),
                    height: parseFloat(formData.measurements.leftWall.height)
                },
                rightWall: {
                    length: parseFloat(formData.measurements.rightWall.length),
                    height: parseFloat(formData.measurements.rightWall.height)
                },
                backWall: {
                    length: parseFloat(formData.measurements.backWall.length),
                    height: parseFloat(formData.measurements.backWall.height)
                },
                trimLinearFeet: parseFloat(formData.measurements.trimLinearFeet) || 0,
                gutterLinearFeet: parseFloat(formData.measurements.gutterLinearFeet) || 0,
                soffitLinearFeet: parseFloat(formData.measurements.soffitLinearFeet) || 0,
                soffitAvgWidthFt: parseFloat(formData.measurements.soffitAvgWidthFt) || 0,
                fasciaLinearFeet: parseFloat(formData.measurements.fasciaLinearFeet) || 0,
                windowCount: parseInt(formData.measurements.windowCount) || 0,
                doorCount: parseInt(formData.measurements.doorCount) || 0,
                garageDoorCount: parseInt(formData.measurements.garageDoorCount) || 0,
                shutterCount: parseInt(formData.measurements.shutterCount) || 0,
                columnsPillarsCount: parseInt(formData.measurements.columnsPillarsCount) || 0,
                columnsPillarsAvgSize: parseFloat(formData.measurements.columnsPillarsAvgSize) || 0,
                porchCeilingsFront: parseFloat(formData.measurements.porchCeilingsFront) || 0,
                porchCeilingsBack: parseFloat(formData.measurements.porchCeilingsBack) || 0,
                poolDeckSqft: parseFloat(formData.measurements.poolDeckSqft) || 0,
                twoToneGuttersSoffits: !!formData.measurements.twoToneGuttersSoffits
            };



            const data = {
                ...(leadId && { leadId }),
                ...formData,
                measurements: baseMeasurements,
                colors: {
                    ...formData.colors,
                    colorComplexity,
                    paintQualityPreference: paintTier
                }
            };

            const result = await submitQuote(data, photos);
            setSubmitResult({
                success: true,
                quoteId: result.quoteId,
                status: result.status,
                message: result.message
            });

            // Reset form
            setFormData({
                customerName: '',
                customerEmail: '',
                customerPhone: '',
                customerAddress: '',
                measurements: {
                    frontWall: { length: '', height: '' },
                    leftWall: { length: '', height: '' },
                    rightWall: { length: '', height: '' },
                    backWall: { length: '', height: '' },
                    stories: 1,
                    trimLinearFeet: '',
                    gutterLinearFeet: '',
                    soffitLinearFeet: '',
                    soffitAvgWidthFt: '',
                    windowCount: '',
                    doorCount: '',
                    garageDoorCount: '',
                    shutterCount: '',
                    fasciaLinearFeet: '',
                    columnsPillarsCount: '',
                    columnsPillarsAvgSize: '',
                    porchCeilingsFront: '',
                    porchCeilingsBack: '',
                    poolDeckSqft: '',
                    twoToneGuttersSoffits: false
                },
                colors: {
                    mainHouse: { code: '', name: '', hex: '' },
                    trim: { code: '', name: '', hex: '' },
                    gutters: { code: '', name: '', hex: '' },
                    soffits: { code: '', name: '', hex: '' },
                    fascia: { code: '', name: '', hex: '' },
                    doors: { code: '', name: '', hex: '' },
                    garageDoors: { code: '', name: '', hex: '' },
                    shutters: { code: '', name: '', hex: '' },
                    porchCeilings: { code: '', name: '', hex: '' },
                    colorComplexity: 'standard'
                }
            });
            setPhotos([]);
            setConsentChecked(false);
            setPaintTier('superpaint');
            setLightColorPrimer(false);
            setMultiColor(false);
            setSameWallHeight(false);
        } catch (error) {
            setSubmitResult({ success: false, error: error.message });
        } finally {
            setSubmitting(false);
        }
    };

    const successMessage = submitResult?.success
        ? (submitResult.quoteId
            ? `${submitResult.message || 'Quote submitted successfully!'} ID: ${submitResult.quoteId}`
            : (submitResult.message || 'Quote submitted successfully!'))
        : '';

    return (
        <div className="worker-view">
            <h1>Submit Quote</h1>

            <form onSubmit={handleSubmit}>
                <section className="lookup-section">
                    <h2>🔍 Lookup Existing Lead</h2>
                    <p className="hint-text">Search by customer email or phone to pre-fill information from a previous estimate.</p>
                    <div className="form-grid">
                        <div>
                            <label>Customer Email</label>
                            <input
                                type="email"
                                placeholder="john@example.com"
                                value={lookupEmail}
                                onChange={(e) => setLookupEmail(e.target.value)}
                            />
                        </div>
                        <div>
                            <label>Customer Phone</label>
                            <input
                                type="tel"
                                placeholder="(555) 123-4567"
                                value={lookupPhone}
                                onChange={(e) => setLookupPhone(e.target.value)}
                            />
                        </div>
                    </div>
                    <button type="button" onClick={handleLeadLookup} disabled={lookupLoading} className="lookup-btn">
                        {lookupLoading ? 'Searching...' : 'Search for Lead'}
                    </button>
                    {lookupResult && (
                        <div className={`lookup-result ${lookupResult.found ? 'success' : 'info'}`}>
                            {lookupResult.found ? (
                                <>
                                    <strong>✓ Lead found!</strong> Customer info pre-filled below.
                                    {lookupResult.lead?.estimateDisplay && (
                                        <span className="estimate-badge">Previous estimate: {lookupResult.lead.estimateDisplay}</span>
                                    )}
                                </>
                            ) : (
                                <span>{lookupResult.message || 'No matching lead found. Enter customer info manually.'}</span>
                            )}
                        </div>
                    )}
                </section>

                <section>
                    <h2>Customer Information</h2>
                    {leadId && <p className="linked-lead">📎 Linked to lead: {leadId}</p>}
                    <div className="form-grid">
                        <div className="form-field">
                            <label>Customer Name *</label>
                            <input
                                type="text"
                                placeholder="John Doe"
                                value={formData.customerName}
                                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                                required
                            />
                        </div>
                        <div className="form-field">
                            <label>Customer Email *</label>
                            <input
                                type="email"
                                placeholder="john@example.com"
                                value={formData.customerEmail}
                                onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                                required
                            />
                        </div>
                        <div className="form-field">
                            <label>Customer Phone *</label>
                            <input
                                type="tel"
                                placeholder="(555) 123-4567"
                                value={formData.customerPhone}
                                onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                                required
                            />
                        </div>
                        <div className="col-span-full form-field">
                            <label>Customer Address *</label>
                            <input
                                type="text"
                                placeholder="123 Main Street, City, State ZIP"
                                value={formData.customerAddress}
                                onChange={(e) => setFormData({ ...formData, customerAddress: e.target.value })}
                                required
                            />
                        </div>
                    </div>
                </section>

                <section>
                    <div className="section-header-row">
                        <h2>House Dimensions</h2>
                        <label className="wall-toggle">
                            <input
                                type="checkbox"
                                checked={sameWallHeight}
                                onChange={(e) => handleSameHeightToggle(e.target.checked)}
                            />
                            <span>Same height for all walls</span>
                        </label>
                    </div>

                    <div className="wall-grid">
                        <div className="wall-card">
                            <h3>Front Wall</h3>
                            <div className="wall-fields">
                                <div className="form-field">
                                    <label>Length (ft)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        placeholder="0"
                                        value={formData.measurements.frontWall.length}
                                        onChange={(e) => handleWallFieldChange('frontWall', 'length', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Height (ft)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        placeholder="0"
                                        value={formData.measurements.frontWall.height}
                                        onChange={(e) => handleWallFieldChange('frontWall', 'height', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="wall-card">
                            <h3>Back Wall</h3>
                            <div className="wall-fields">
                                <div className="form-field">
                                    <label>Length (ft)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        placeholder="0"
                                        value={formData.measurements.backWall.length}
                                        onChange={(e) => handleWallFieldChange('backWall', 'length', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Height (ft)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        placeholder="0"
                                        value={formData.measurements.backWall.height}
                                        onChange={(e) => handleWallFieldChange('backWall', 'height', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="wall-card">
                            <h3>Left Wall</h3>
                            <div className="wall-fields">
                                <div className="form-field">
                                    <label>Length (ft)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        placeholder="0"
                                        value={formData.measurements.leftWall.length}
                                        onChange={(e) => handleWallFieldChange('leftWall', 'length', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Height (ft)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        placeholder="0"
                                        value={formData.measurements.leftWall.height}
                                        onChange={(e) => handleWallFieldChange('leftWall', 'height', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="wall-card">
                            <h3>Right Wall</h3>
                            <div className="wall-fields">
                                <div className="form-field">
                                    <label>Length (ft)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        placeholder="0"
                                        value={formData.measurements.rightWall.length}
                                        onChange={(e) => handleWallFieldChange('rightWall', 'length', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Height (ft)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        placeholder="0"
                                        value={formData.measurements.rightWall.height}
                                        onChange={(e) => handleWallFieldChange('rightWall', 'height', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="stories-panel">
                        <div className="form-field">
                            <label>Number of Stories *</label>
                            <select
                                value={formData.measurements.stories}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    measurements: { ...formData.measurements, stories: parseInt(e.target.value) }
                                })}
                            >
                                <option value="1">1 Story</option>
                                <option value="2">2 Stories</option>
                                <option value="3">3 Stories</option>
                            </select>
                        </div>
                    </div>
                </section>

                <section>
                    <h2>Additional Measurements</h2>

                    <h3 className="subsection-title">Linear Measurements</h3>
                    <div className="measurement-grid">
                        <div className="measurement-card">
                            <div className="form-field">
                                <label>Trim (linear ft)</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    placeholder="0"
                                    value={formData.measurements.trimLinearFeet}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        measurements: { ...formData.measurements, trimLinearFeet: e.target.value }
                                    })}
                                />
                            </div>
                            <ColorSelector
                                label="Trim Color" compact={true}
                                value={formData.colors.trim.code && formData.colors.trim.name ? {
                                    code: formData.colors.trim.code,
                                    name: formData.colors.trim.name,
                                    hex: formData.colors.trim.hex
                                } : null}
                                onChange={(color) => setFormData({
                                    ...formData,
                                    colors: {
                                        ...formData.colors,
                                        trim: color ? { code: color.code, name: color.name, hex: color.hex } : { code: '', name: '', hex: '' }
                                    }
                                })}
                            />
                        </div>

                        <div className="measurement-card">
                            <div className="form-field">
                                <label>Gutters (linear ft)</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    placeholder="0"
                                    value={formData.measurements.gutterLinearFeet}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        measurements: { ...formData.measurements, gutterLinearFeet: e.target.value }
                                    })}
                                />
                            </div>
                            <ColorSelector
                                label="Gutter Color" compact={true}
                                value={formData.colors.gutters.code && formData.colors.gutters.name ? {
                                    code: formData.colors.gutters.code,
                                    name: formData.colors.gutters.name,
                                    hex: formData.colors.gutters.hex
                                } : null}
                                onChange={(color) => setFormData({
                                    ...formData,
                                    colors: {
                                        ...formData.colors,
                                        gutters: color ? { code: color.code, name: color.name, hex: color.hex } : { code: '', name: '', hex: '' }
                                    }
                                })}
                            />
                        </div>

                        <div className="measurement-card">
                            <div className="inline-field-row">
                                <div className="form-field">
                                    <label>Soffits <span className="label-unit">(linear ft)</span></label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        placeholder="0"
                                        value={formData.measurements.soffitLinearFeet}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            measurements: { ...formData.measurements, soffitLinearFeet: e.target.value }
                                        })}
                                    />
                                </div>
                                <div className="form-field">
                                    <label className="sub-label">Avg Width (ft)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        placeholder="0"
                                        value={formData.measurements.soffitAvgWidthFt}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            measurements: { ...formData.measurements, soffitAvgWidthFt: e.target.value }
                                        })}
                                    />
                                </div>
                            </div>
                            <ColorSelector
                                label="Soffit Color" compact={true}
                                value={formData.colors.soffits.code && formData.colors.soffits.name ? {
                                    code: formData.colors.soffits.code,
                                    name: formData.colors.soffits.name,
                                    hex: formData.colors.soffits.hex
                                } : null}
                                onChange={(color) => setFormData({
                                    ...formData,
                                    colors: {
                                        ...formData.colors,
                                        soffits: color ? { code: color.code, name: color.name, hex: color.hex } : { code: '', name: '', hex: '' }
                                    }
                                })}
                            />
                        </div>

                        <div className="measurement-card">
                            <div className="form-field">
                                <label>Fascia (linear ft)</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    placeholder="0"
                                    value={formData.measurements.fasciaLinearFeet}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        measurements: { ...formData.measurements, fasciaLinearFeet: e.target.value }
                                    })}
                                />
                            </div>
                            <ColorSelector
                                label="Fascia Color" compact={true}
                                value={formData.colors.fascia.code && formData.colors.fascia.name ? {
                                    code: formData.colors.fascia.code,
                                    name: formData.colors.fascia.name,
                                    hex: formData.colors.fascia.hex
                                } : null}
                                onChange={(color) => setFormData({
                                    ...formData,
                                    colors: {
                                        ...formData.colors,
                                        fascia: color ? { code: color.code, name: color.name, hex: color.hex } : { code: '', name: '', hex: '' }
                                    }
                                })}
                            />
                        </div>

                        <div className="col-span-full color-adjustments">
                            <label className="color-adjustment">
                                <input
                                    type="checkbox"
                                    checked={formData.measurements.twoToneGuttersSoffits}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        measurements: { ...formData.measurements, twoToneGuttersSoffits: e.target.checked }
                                    })}
                                />
                                <span>Two-tone gutters/soffits (+$250)</span>
                            </label>
                        </div>
                    </div>

                    <h3 className="subsection-title">Openings & Features</h3>
                    <div className="measurement-grid">
                        <div className="feature-card">
                            <CountStepper
                                id="doorCount"
                                label="Doors"
                                value={formData.measurements.doorCount}
                                onChange={(value) => handleCountInputChange('doorCount', value)}
                                onStep={(delta) => handleCountStep('doorCount', delta)}
                            />
                            <ColorSelector
                                label="Door Color" compact={true}
                                value={formData.colors.doors.code && formData.colors.doors.name ? {
                                    code: formData.colors.doors.code,
                                    name: formData.colors.doors.name,
                                    hex: formData.colors.doors.hex
                                } : null}
                                onChange={(color) => setFormData({
                                    ...formData,
                                    colors: {
                                        ...formData.colors,
                                        doors: color ? { code: color.code, name: color.name, hex: color.hex } : { code: '', name: '', hex: '' }
                                    }
                                })}
                            />
                        </div>

                        <div className="feature-card">
                            <CountStepper
                                id="garageDoorCount"
                                label="Garage Doors"
                                value={formData.measurements.garageDoorCount}
                                onChange={(value) => handleCountInputChange('garageDoorCount', value)}
                                onStep={(delta) => handleCountStep('garageDoorCount', delta)}
                            />
                            <ColorSelector
                                label="Garage Door Color" compact={true}
                                value={formData.colors.garageDoors.code && formData.colors.garageDoors.name ? {
                                    code: formData.colors.garageDoors.code,
                                    name: formData.colors.garageDoors.name,
                                    hex: formData.colors.garageDoors.hex
                                } : null}
                                onChange={(color) => setFormData({
                                    ...formData,
                                    colors: {
                                        ...formData.colors,
                                        garageDoors: color ? { code: color.code, name: color.name, hex: color.hex } : { code: '', name: '', hex: '' }
                                    }
                                })}
                            />
                        </div>

                        <div className="feature-card">
                            <CountStepper
                                id="shutterCount"
                                label="Shutters"
                                value={formData.measurements.shutterCount}
                                onChange={(value) => handleCountInputChange('shutterCount', value)}
                                onStep={(delta) => handleCountStep('shutterCount', delta)}
                            />
                            <ColorSelector
                                label="Shutter Color" compact={true}
                                value={formData.colors.shutters.code && formData.colors.shutters.name ? {
                                    code: formData.colors.shutters.code,
                                    name: formData.colors.shutters.name,
                                    hex: formData.colors.shutters.hex
                                } : null}
                                onChange={(color) => setFormData({
                                    ...formData,
                                    colors: {
                                        ...formData.colors,
                                        shutters: color ? { code: color.code, name: color.name, hex: color.hex } : { code: '', name: '', hex: '' }
                                    }
                                })}
                            />
                        </div>

                        <div className="feature-card">
                            <CountStepper
                                id="windowCount"
                                label="Number of Windows"
                                value={formData.measurements.windowCount}
                                onChange={(value) => handleCountInputChange('windowCount', value)}
                                onStep={(delta) => handleCountStep('windowCount', delta)}
                            />
                        </div>
                    </div>

                    <h3 className="subsection-title">Other Structures</h3>
                    <div className="measurement-grid">
                        <div className="feature-card">
                            <CountStepper
                                id="columnsPillarsCount"
                                label="Columns/Pillars"
                                value={formData.measurements.columnsPillarsCount}
                                onChange={(value) => handleCountInputChange('columnsPillarsCount', value)}
                                onStep={(delta) => handleCountStep('columnsPillarsCount', delta)}
                            />
                            <div className="form-field mt-2">
                                <label className="sub-label">Avg Size (ft)</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    placeholder="0"
                                    value={formData.measurements.columnsPillarsAvgSize}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        measurements: { ...formData.measurements, columnsPillarsAvgSize: e.target.value }
                                    })}
                                />
                            </div>
                        </div>

                        <div className="measurement-card">
                            <div className="form-field">
                                <label>Porch Ceilings (sqft)</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    placeholder="0"
                                    value={(parseFloat(formData.measurements.porchCeilingsFront) || 0) + (parseFloat(formData.measurements.porchCeilingsBack) || 0)}
                                    onChange={(e) => {
                                        const total = parseFloat(e.target.value) || 0;
                                        setFormData({
                                            ...formData,
                                            measurements: {
                                                ...formData.measurements,
                                                porchCeilingsFront: total,
                                                porchCeilingsBack: 0
                                            }
                                        });
                                    }}
                                />
                            </div>
                            <ColorSelector
                                label="Ceiling Color" compact={true}
                                value={formData.colors.porchCeilings.code && formData.colors.porchCeilings.name ? {
                                    code: formData.colors.porchCeilings.code,
                                    name: formData.colors.porchCeilings.name,
                                    hex: formData.colors.porchCeilings.hex
                                } : null}
                                onChange={(color) => setFormData({
                                    ...formData,
                                    colors: {
                                        ...formData.colors,
                                        porchCeilings: color ? { code: color.code, name: color.name, hex: color.hex } : { code: '', name: '', hex: '' }
                                    }
                                })}
                            />
                        </div>

                        <div className="measurement-card">
                            <div className="form-field">
                                <label>Pool Deck (sqft)</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    placeholder="0"
                                    value={formData.measurements.poolDeckSqft}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        measurements: { ...formData.measurements, poolDeckSqft: e.target.value }
                                    })}
                                />
                            </div>
                        </div>
                    </div>
                </section>

                <section>
                    <h2>Paint Tier (Customer Preference)</h2>
                    <div className="paint-tier-grid">
                        {paintTiers.map((tier) => (
                            <button
                                key={tier.value}
                                type="button"
                                className={`paint-tier-card ${paintTier === tier.value ? 'selected' : ''}`}
                                onClick={() => setPaintTier(tier.value)}
                            >
                                <div className="paint-tier-title">
                                    {tier.name}
                                    {tier.badge && <span className="paint-tier-badge">{tier.badge}</span>}
                                </div>
                                <div className="paint-tier-rate">${tier.rate.toFixed(2)}/sqft</div>
                            </button>
                        ))}
                    </div>
                </section>


                <section>
                    <h2>Colors</h2>
                    <ColorSelector
                        label="Main House Color"
                        value={formData.colors.mainHouse.code && formData.colors.mainHouse.name ? {
                            code: formData.colors.mainHouse.code,
                            name: formData.colors.mainHouse.name,
                            hex: formData.colors.mainHouse.hex
                        } : null}
                        onChange={(color) => setFormData({
                            ...formData,
                            colors: {
                                ...formData.colors,
                                mainHouse: color ? { code: color.code, name: color.name, hex: color.hex } : { code: '', name: '', hex: '' }
                            }
                        })}
                        required
                    />

                    <div className="color-adjustments">
                        <label className="color-adjustment">
                            <input
                                type="checkbox"
                                checked={lightColorPrimer}
                                onChange={(e) => setLightColorPrimer(e.target.checked)}
                            />
                            <span>Light color / primer required (+$0.15/sqft)</span>
                        </label>
                        <label className="color-adjustment">
                            <input
                                type="checkbox"
                                checked={multiColor}
                                onChange={(e) => setMultiColor(e.target.checked)}
                            />
                            <span>More than 3 colors (+$0.25/sqft)</span>
                        </label>
                    </div>
                </section>

                <section>
                    <h2>Photos</h2>
                    <p className="consent-text">Photos are optional. If provided, they will be analyzed to improve accuracy.</p>
                    <PhotoUpload photos={photos} setPhotos={setPhotos} />

                </section>

                <section className="consent-section">
                    <p className="consent-text">
                        <strong>Privacy Notice:</strong> If photos are submitted, they will be analyzed by AI to assess property condition and generate accurate pricing. Customer personal information (name, email, phone, address) is <strong>not</strong> shared with the AI system — only photos and non-identifying measurements are used.
                    </p>
                    <label className="consent-label">
                        <input
                            type="checkbox"
                            checked={consentChecked}
                            onChange={(e) => setConsentChecked(e.target.checked)}
                            className="consent-checkbox"
                        />
                        <span className="consent-note">
                            I confirm the customer has been informed that any submitted photos will be analyzed by AI for quote generation purposes.
                        </span>
                    </label>
                </section>

                <button type="submit" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Quote'}
                </button>
                {submitResult && (
                    <div className={`alert ${submitResult.success ? 'success' : 'error'}`} style={{ marginTop: '16px' }}>
                        {submitResult.success ? successMessage : `Error: ${submitResult.error}`}
                    </div>
                )}
            </form>
        </div>
    );
}
